import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../api/apiClient";
import { FeatureCode } from "../constants/constants";
import { useAuth } from "./AuthContext";

interface FeatureProfile {
  featureProfileId: number;
  code: string;
  name: string;
  description?: string;
}

interface TenantAccess {
  tenantId: number;
  profile: FeatureProfile | null;
  features: string[];
}

interface AccessContextValue {
  loading: boolean;
  profile: FeatureProfile | null;
  features: ReadonlySet<string>;
  hasFeature: (featureCode: FeatureCode | string) => boolean;
  hasAnyFeature: (featureCodes: readonly (FeatureCode | string)[]) => boolean;
  refresh: () => Promise<void>;
}

const AccessContext = createContext<AccessContextValue | undefined>(undefined);

export function AccessProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, selectedTenant } = useAuth();
  const [access, setAccess] = useState<TenantAccess | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    if (!isAuthenticated || !selectedTenant) {
      setAccess(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setAccess(await api<TenantAccess>("/api/me/access", {
        suppressLoading: true,
      }));
    } catch (error) {
      console.error("Failed to load tenant feature access", error);
      setAccess(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // Access must be reloaded whenever the active tenant changes.
  }, [isAuthenticated, selectedTenant?.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo<AccessContextValue>(() => {
    const features = new Set(access?.features ?? []);
    return {
      loading,
      profile: access?.profile ?? null,
      features,
      hasFeature: (featureCode) => features.has(featureCode),
      hasAnyFeature: (featureCodes) =>
        featureCodes.some((featureCode) => features.has(featureCode)),
      refresh,
    };
  }, [access, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess() {
  const context = useContext(AccessContext);
  if (!context) throw new Error("useAccess must be used within AccessProvider");
  return context;
}
